export interface PhotoWithOrder {
  id: number;
  order: number;
  url_l: string;
  url_m: string;
  url_s: string;
}

export interface PhotoReorderEntry {
  id: number;
  newOrder: number;
}

export type PhotoReorderValidationError =
  | "EMPTY_QUEUE"
  | "INVALID_NEW_ORDER_RANGE"
  | "DUPLICATE_ID"
  | "DUPLICATE_NEW_ORDER";

function compareExistingOrder(
  a: { order?: number },
  b: { order?: number }
): number {
  const orderA =
    typeof a.order === "number" && Number.isFinite(a.order)
      ? a.order
      : Number.POSITIVE_INFINITY;
  const orderB =
    typeof b.order === "number" && Number.isFinite(b.order)
      ? b.order
      : Number.POSITIVE_INFINITY;
  return orderA - orderB;
}

/**
 * Display/API order is 1-based. Sort by the stored `order` first so drag-and-drop
 * matches what the user sees, then reindex to a contiguous sequence.
 */
export function normalizePhotoOrder<T extends { id: number; order?: number }>(
  photos: readonly T[]
): PhotoWithOrder[] {
  return [...photos].sort(compareExistingOrder).map((photo, index) => {
    const withUrls = photo as T & Partial<PhotoWithOrder>;
    return {
      id: photo.id,
      order: index + 1,
      url_l: withUrls.url_l ?? "",
      url_m: withUrls.url_m ?? "",
      url_s: withUrls.url_s ?? "",
    };
  });
}

export function getPhotoOrderBaseline(photos: readonly { id: number }[]): number[] {
  return photos.map((photo) => photo.id);
}

export function hasPhotoOrderChanged(
  baselineIds: readonly number[],
  photos: readonly { id: number }[]
): boolean {
  if (baselineIds.length !== photos.length) return true;
  return baselineIds.some((id, index) => id !== photos[index]?.id);
}

export function reorderPhotosAfterDrop<T extends PhotoWithOrder>(
  photos: readonly T[],
  dragId: string,
  dropTargetId: string
): { photos: T[]; queue: PhotoReorderEntry[] } {
  const orderedPhotos = [...photos].sort((a, b) => a.order - b.order);
  const dragIndex = orderedPhotos.findIndex(
    (photo) => String(photo.id) === dragId
  );
  const dropIndex = orderedPhotos.findIndex(
    (photo) => String(photo.id) === dropTargetId
  );

  if (dragIndex < 0 || dropIndex < 0 || dragIndex === dropIndex) {
    return { photos: [...photos], queue: [] };
  }

  const movedPhotos = [...orderedPhotos];
  const [movedPhoto] = movedPhotos.splice(dragIndex, 1);
  movedPhotos.splice(dropIndex, 0, movedPhoto);

  const normalizedPhotos = movedPhotos.map((photo, index) => ({
    ...photo,
    order: index + 1,
  }));

  const queue = normalizedPhotos
    .filter((photo) => {
      const previousPhoto = orderedPhotos.find((item) => item.id === photo.id);
      return !!previousPhoto && previousPhoto.order !== photo.order;
    })
    .map((photo) => ({ id: photo.id, newOrder: photo.order }));

  return { photos: normalizedPhotos, queue };
}

export function buildPhotoReorderPayload(
  baselineIds: readonly number[],
  photos: readonly PhotoWithOrder[]
): PhotoReorderEntry[] {
  const orderedPhotos = [...photos].sort((a, b) => a.order - b.order);

  if (!hasPhotoOrderChanged(baselineIds, orderedPhotos)) {
    return [];
  }

  return orderedPhotos
    .map((photo, index) => {
      const baselineIndex = baselineIds.indexOf(photo.id);
      const newOrder = index + 1;
      if (baselineIndex === index) return null;
      return { id: photo.id, newOrder };
    })
    .filter((entry): entry is PhotoReorderEntry => entry != null);
}

export function sanitizePhotoReorderQueue(
  queue: readonly PhotoReorderEntry[],
  existingPhotoIds: ReadonlySet<number> | readonly number[]
): PhotoReorderEntry[] {
  const idSet =
    existingPhotoIds instanceof Set
      ? existingPhotoIds
      : new Set(existingPhotoIds);
  return queue.filter((entry) => idSet.has(entry.id));
}

export function validatePhotoReorderQueue(
  queue: readonly PhotoReorderEntry[],
  photoCount: number
): { ok: true } | { ok: false; error: PhotoReorderValidationError } {
  if (queue.length < 1) {
    return { ok: false, error: "EMPTY_QUEUE" };
  }

  if (photoCount < 1) {
    return { ok: false, error: "EMPTY_QUEUE" };
  }

  const ids = queue.map((entry) => entry.id);
  if (new Set(ids).size !== ids.length) {
    return { ok: false, error: "DUPLICATE_ID" };
  }

  const orders = queue.map((entry) => entry.newOrder);
  if (new Set(orders).size !== orders.length) {
    return { ok: false, error: "DUPLICATE_NEW_ORDER" };
  }

  for (const entry of queue) {
    const { newOrder } = entry;
    if (
      !Number.isInteger(newOrder) ||
      newOrder < 1 ||
      newOrder > photoCount
    ) {
      return { ok: false, error: "INVALID_NEW_ORDER_RANGE" };
    }
  }

  return { ok: true };
}

export function buildPhotoReorderApiBody(queue: readonly PhotoReorderEntry[]): {
  photos: PhotoReorderEntry[];
} {
  return { photos: [...queue] };
}
